(() => {
  const SELECTORS = {
    modal: '[data-bs-media-modal]',
    backdrop: '[data-bs-media-backdrop]',
    close: '[data-bs-media-close]',
    stage: '[data-bs-media-stage]',
    tabs: '[data-bs-media-tab]',
    thumbsWrap: '[data-bs-media-thumbs]',
    thumb: '[data-bs-media-thumb]',
    templates: '[data-bs-media-template]',
    zoomTools: '[data-bs-media-zoom-tools]',
    zoomFab: '[data-bs-media-zoom-fab]',
    zoomBtn: '[data-bs-media-zoom]',
  };

  function qs(root, sel) {
    return root.querySelector(sel);
  }

  function qsa(root, sel) {
    return Array.from(root.querySelectorAll(sel));
  }

  function getFocusable(root) {
    return qsa(
      root,
      [
        'button:not([disabled])',
        '[href]',
        'input:not([disabled])',
        'select:not([disabled])',
        'textarea:not([disabled])',
        '[tabindex]:not([tabindex="-1"])',
      ].join(',')
    ).filter((el) => el.offsetParent !== null);
  }

  function lockScroll(lock) {
    document.documentElement.style.overflow = lock ? 'hidden' : '';
    document.body.style.overflow = lock ? 'hidden' : '';
  }

  function safePauseMedia(container) {
    qsa(container, 'video').forEach((v) => {
      try {
        v.pause();
      } catch (_) {}
    });
  }

  class BoldstuffMediaModal {
    constructor(modalEl) {
      this.el = modalEl;
      this.dialog = qs(modalEl, '.bs-media-modal__dialog');
      this.backdrop = qs(modalEl, SELECTORS.backdrop);
      this.closeBtn = qs(modalEl, SELECTORS.close);
      this.stage = qs(modalEl, SELECTORS.stage);
      this.thumbsWrap = qs(modalEl, SELECTORS.thumbsWrap);
      this.zoomTools = qs(modalEl, SELECTORS.zoomTools);
      this.zoomFab = qs(modalEl, SELECTORS.zoomFab);
      this.activeTab = 'images';
      this.activeMediaId = null;
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      this.lastFocus = null;

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onClick = this.onClick.bind(this);

      this.el.addEventListener('click', this.onClick);
      document.addEventListener('keydown', this.onKeyDown);
    }

    destroy() {
      this.el.removeEventListener('click', this.onClick);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    onClick(e) {
      const close = e.target.closest(SELECTORS.close);
      if (close) {
        this.hide();
        return;
      }

      const backdrop = e.target.closest(SELECTORS.backdrop);
      if (backdrop) {
        this.hide();
        return;
      }

      const tab = e.target.closest(SELECTORS.tabs);
      if (tab) {
        this.setTab(tab.getAttribute('data-bs-media-tab'));
        return;
      }

      const thumb = e.target.closest(SELECTORS.thumb);
      if (thumb) {
        const id = thumb.getAttribute('data-media-id');
        const tabTarget = thumb.getAttribute('data-bs-media-tab-target') || 'images';
        if (tabTarget !== this.activeTab) this.setTab(tabTarget);
        this.setActiveMedia(id);
        return;
      }

      const zoomBtn = e.target.closest(SELECTORS.zoomBtn);
      if (zoomBtn) {
        const dir = zoomBtn.getAttribute('data-bs-media-zoom');
        this.zoom(dir === 'in' ? 1 : -1);
        return;
      }

      const zoomFab = e.target.closest(SELECTORS.zoomFab);
      if (zoomFab) {
        // Simple toggle for convenience
        if (this.scale === 1) this.zoom(1);
        else this.resetZoom();
      }
    }

    onKeyDown(e) {
      if (!this.isOpen()) return;

      if (e.key === 'Escape') {
        e.preventDefault();
        this.hide();
        return;
      }

      if (e.key === 'Tab') {
        const focusable = getFocusable(this.dialog);
        if (!focusable.length) return;

        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        const active = document.activeElement;

        if (e.shiftKey) {
          if (active === first || !this.dialog.contains(active)) {
            e.preventDefault();
            last.focus();
          }
        } else {
          if (active === last) {
            e.preventDefault();
            first.focus();
          }
        }
      }

      if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
        // Optional: keyboard thumb navigation when focus is on thumbs
        const activeThumb = this.thumbsWrap && this.thumbsWrap.querySelector('.bs-media-modal__thumb.is-active');
        if (!activeThumb) return;
        if (!this.thumbsWrap.contains(document.activeElement)) return;

        const visibleThumbs = this.getVisibleThumbs();
        const idx = visibleThumbs.indexOf(activeThumb);
        if (idx === -1) return;

        e.preventDefault();
        const nextIdx = e.key === 'ArrowDown' ? Math.min(visibleThumbs.length - 1, idx + 1) : Math.max(0, idx - 1);
        const next = visibleThumbs[nextIdx];
        if (next) {
          next.focus();
          next.click();
        }
      }
    }

    isOpen() {
      return this.el.classList.contains('is-open');
    }

    show({ mediaId, tab = 'images', openerEl } = {}) {
      this.lastFocus = openerEl || document.activeElement;
      this.el.setAttribute('aria-hidden', 'false');
      this.el.classList.add('is-open');
      lockScroll(true);

      this.setTab(tab);

      const initial = mediaId || this.getFirstMediaIdForTab(tab) || this.getAnyMediaId();
      if (initial) this.setActiveMedia(initial);

      // Focus close for immediate escape hatch
      if (this.closeBtn) this.closeBtn.focus();
    }

    hide() {
      if (!this.isOpen()) return;

      safePauseMedia(this.stage);
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      lockScroll(false);
      this.resetZoom();

      if (this.lastFocus && typeof this.lastFocus.focus === 'function') {
        this.lastFocus.focus();
      }
    }

    getVisibleThumbs() {
      if (!this.thumbsWrap) return [];
      return qsa(this.thumbsWrap, SELECTORS.thumb).filter((btn) => {
        const t = btn.getAttribute('data-bs-media-tab-target') || 'images';
        return t === this.activeTab;
      });
    }

    getFirstMediaIdForTab(tab) {
      const btn = qsa(this.el, SELECTORS.thumb).find((b) => (b.getAttribute('data-bs-media-tab-target') || 'images') === tab);
      return btn ? btn.getAttribute('data-media-id') : null;
    }

    getAnyMediaId() {
      const btn = qs(this.el, SELECTORS.thumb);
      return btn ? btn.getAttribute('data-media-id') : null;
    }

    setTab(tab) {
      const next = tab === 'videos' ? 'videos' : 'images';
      // If theme didn't render the VIDEOS tab (no videos), always stay on IMAGES.
      if (next === 'videos' && !qs(this.el, `${SELECTORS.tabs}[data-bs-media-tab="videos"]`)) {
        this.activeTab = 'images';
      } else {
      this.activeTab = next;
      }

      qsa(this.el, SELECTORS.tabs).forEach((btn) => {
        const isActive = btn.getAttribute('data-bs-media-tab') === this.activeTab;
        btn.classList.toggle('is-active', isActive);
        btn.setAttribute('aria-selected', isActive ? 'true' : 'false');
      });

      qsa(this.el, SELECTORS.thumb).forEach((btn) => {
        const t = btn.getAttribute('data-bs-media-tab-target') || 'images';
        btn.style.display = t === this.activeTab ? '' : 'none';
      });

      // If current active media is not in this tab, switch to first in tab
      const activeThumb = this.getThumbById(this.activeMediaId);
      const activeThumbTab = activeThumb ? activeThumb.getAttribute('data-bs-media-tab-target') : null;
      if (!activeThumb || activeThumbTab !== this.activeTab) {
        const first = this.getFirstMediaIdForTab(this.activeTab);
        if (first) this.setActiveMedia(first);
      }
    }

    getThumbById(mediaId) {
      if (!mediaId) return null;
      return qsa(this.el, SELECTORS.thumb).find((b) => b.getAttribute('data-media-id') === String(mediaId)) || null;
    }

    getTemplateById(mediaId) {
      if (!mediaId) return null;
      return qsa(this.el, SELECTORS.templates).find((t) => t.getAttribute('data-media-id') === String(mediaId)) || null;
    }

    setActiveMedia(mediaId) {
      const id = String(mediaId || '');
      const tpl = this.getTemplateById(id);
      if (!tpl || !this.stage) return;

      this.activeMediaId = id;
      this.resetZoom();
      safePauseMedia(this.stage);
      this.stage.innerHTML = '';
      this.stage.appendChild(tpl.content.cloneNode(true));

      qsa(this.el, SELECTORS.thumb).forEach((b) => b.classList.toggle('is-active', b.getAttribute('data-media-id') === id));

      const type = tpl.getAttribute('data-media-type') || 'image';
      const isImage = type === 'image';
      this.el.setAttribute('data-active-media-type', type);
      if (this.zoomTools) this.zoomTools.hidden = !isImage;
      if (this.zoomFab) this.zoomFab.hidden = !isImage;

      if (isImage) {
        const img = qs(this.stage, '.bs-media-modal__img');
        if (img) this.attachImagePanZoom(img);
      }

      // Autoplay where possible
      const video = qs(this.stage, 'video');
      if (video) {
        video.muted = true;
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === 'function') playPromise.catch(() => {});
      }
    }

    zoom(dir) {
      const img = qs(this.stage, '.bs-media-modal__img');
      if (!img) return;
      const next = Math.min(4, Math.max(1, this.scale + (dir > 0 ? 0.5 : -0.5)));
      this.scale = next;
      if (this.scale === 1) {
        this.panX = 0;
        this.panY = 0;
      }
      this.applyImageTransform(img, false);
    }

    resetZoom() {
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      const img = this.stage ? qs(this.stage, '.bs-media-modal__img') : null;
      if (img) this.applyImageTransform(img, false);
    }

    attachImagePanZoom(img) {
      // Ensure clean initial state (original size within container; no pan)
      this.scale = 1;
      this.panX = 0;
      this.panY = 0;
      img.style.cursor = 'zoom-in';
      this.applyImageTransform(img, false);

      // Double click toggles zoom (Amazon-like)
      img.ondblclick = (e) => {
        e.preventDefault();
        if (this.scale === 1) {
          this.scale = 2.5;
          img.style.cursor = 'zoom-out';
        } else {
          this.scale = 1;
          this.panX = 0;
          this.panY = 0;
          img.style.cursor = 'zoom-in';
        }
        this.applyImageTransform(img, true);
      };

      // Hover pan only when zoomed
      img.onmousemove = (e) => {
        if (this.scale <= 1) return;
        const stage = img.closest('.bs-media-modal__stage-inner') || this.stage;
        if (!stage) return;
        const rect = stage.getBoundingClientRect();
        const relX = (e.clientX - rect.left) / rect.width; // 0..1
        const relY = (e.clientY - rect.top) / rect.height; // 0..1

        // Compute max translate so image edges stay within view
        const maxPanX = Math.max(0, (img.clientWidth * (this.scale - 1)) / 2);
        const maxPanY = Math.max(0, (img.clientHeight * (this.scale - 1)) / 2);

        this.panX = (0.5 - relX) * 2 * maxPanX;
        this.panY = (0.5 - relY) * 2 * maxPanY;
        this.applyImageTransform(img, true);
      };

      img.onmouseleave = () => {
        if (this.scale <= 1) return;
        // gently recenter
        this.panX = 0;
        this.panY = 0;
        this.applyImageTransform(img, true);
      };
    }

    applyImageTransform(img, smooth) {
      img.style.transition = smooth ? 'transform 120ms ease-out' : 'transform 160ms ease';
      // translate first so it feels like panning a zoomed image
      img.style.transform = `translate3d(${this.panX}px, ${this.panY}px, 0) scale(${this.scale})`;
      img.style.cursor = this.scale > 1 ? 'zoom-out' : 'zoom-in';
    }
  }

  function getClosestModalFor(el) {
    if (!el) return null;
    // Prefer modal inside same section; fallback to first on page
    const section = el.closest('.shopify-section') || el.closest('section') || el.closest('[data-section-id]');
    if (section) {
      const m = qs(section, SELECTORS.modal);
      if (m) return m;
    }
    return qs(document, SELECTORS.modal);
  }

  const instances = new WeakMap();
  function getInstance(modalEl) {
    if (!modalEl) return null;
    if (!instances.has(modalEl)) instances.set(modalEl, new BoldstuffMediaModal(modalEl));
    return instances.get(modalEl);
  }

  function mediaIdFromClickTarget(target) {
    if (!target) return null;

    // boldstuff-product-main gallery
    const thumb = target.closest('.thumbnail[data-media-id], .gallery-main-media[data-media-index]');
    if (thumb && thumb.getAttribute('data-media-id')) return thumb.getAttribute('data-media-id');

    // boldstuff-product-page gallery
    const pageMedia = target.closest('.boldstuff-product-page [data-media-id]');
    if (pageMedia && pageMedia.getAttribute('data-media-id')) return pageMedia.getAttribute('data-media-id');

    // main-product media list
    const mediaItem = target.closest('.product__media-item[data-media-id]');
    if (mediaItem) {
      const raw = mediaItem.getAttribute('data-media-id'); // "sectionId-mediaId"
      if (raw && raw.includes('-')) return raw.split('-').slice(1).join('-');
    }

    // product-thumbnail "open modal" button includes data-media-id for model; fallback
    const btn = target.closest('[data-media-id]');
    if (btn) return btn.getAttribute('data-media-id');

    return null;
  }

  function tabFromMediaType(mediaType) {
    return mediaType === 'video' || mediaType === 'external_video' ? 'videos' : 'images';
  }

  function mediaTypeFromThumbOrTemplate(modalEl, mediaId) {
    const tpl = qsa(modalEl, SELECTORS.templates).find((t) => t.getAttribute('data-media-id') === String(mediaId));
    return tpl ? tpl.getAttribute('data-media-type') : null;
  }

  document.addEventListener('click', (e) => {
    const target = e.target;

    // Open from boldstuff-product-main main stage or thumbnails
    const openTriggers = [
      '.product-media-gallery .gallery-main',
      '.product-media-gallery .thumbnail[data-media-id]',
      '.boldstuff-product-page .main-image-wrapper',
      '.boldstuff-product-page .thumbnail-item',
      '.product__media-list .product__media-item',
      '#main-media-container .product__media-item',
      '.product__modal-opener',
    ];

    const isTrigger = openTriggers.some((sel) => target.closest(sel));
    if (!isTrigger) return;

    const modalEl = getClosestModalFor(target);
    if (!modalEl) return;

    const mediaId = mediaIdFromClickTarget(target);
    if (!mediaId) return;

    // Avoid hijacking existing 3d-model modal button clicks
    const mediaType = mediaTypeFromThumbOrTemplate(modalEl, mediaId) || (target.closest('[data-media-type]') && target.closest('[data-media-type]').getAttribute('data-media-type'));
    const tab = tabFromMediaType(mediaType);

    e.preventDefault();
    e.stopPropagation();

    getInstance(modalEl).show({ mediaId, tab, openerEl: target.closest('button, a, [tabindex]') || target });
  }, { capture: true });

  // Expose for debugging / future usage
  window.BoldstuffMediaModal = { getInstance };
})();

